import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { onboardingStepShellClass } from '../Utils/onboardingLayout';
import type { ListedInventoryItem } from './AddItemsForm';

interface ParLevelsStepProps {
  items: ListedInventoryItem[];
  onUpdatePar: (item: ListedInventoryItem, parLevel: number) => Promise<void>;
  onNext: () => void;
  onPrev: () => void;
}

export const ParLevelsStep = ({
  items,
  onUpdatePar,
  onNext,
  onPrev
}: ParLevelsStepProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);

  if (items.length === 0) {
    return (
      <div className={onboardingStepShellClass}>
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
            Par levels
          </h2>
          <p className="mt-1 text-sm text-[#6f747c]">
            No items found. Go back and add some first.
          </p>
        </div>
        <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
          <button
            type="button"
            onClick={onPrev}
            className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] hover:bg-[#f0f2f5]"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            className="ml-auto rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white hover:bg-[#066873]"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  if (currentIndex >= items.length) {
    return (
      <div className={onboardingStepShellClass}>
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
            All pars set.
          </h2>
          <p className="mt-1 text-sm text-[#6f747c]">
            {items.length} item{items.length !== 1 ? 's' : ''} configured.
          </p>
        </div>
        <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
          <button
            type="button"
            onClick={onNext}
            className="ml-auto rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white hover:bg-[#066873]"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  const par = currentItem.parLevel ?? 0;

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex(p => p + 1);
  };

  const handlePrev = () => {
    if (currentIndex === 0) onPrev();
    else {
      setDirection(-1);
      setCurrentIndex(p => p - 1);
    }
  };

  const savePar = async (val: number) => {
    if (saving) return;
    setSaving(true);
    try {
      await onUpdatePar(currentItem, Math.max(0, val));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={onboardingStepShellClass}>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
          Par levels
        </h2>
        <p className="mt-1 text-sm text-[#6f747c]">
          Minimum on hand before you reorder.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-xs text-[#6f747c]">
          <span>{currentItem.categoryName || 'Uncategorized'}</span>
          <span>
            {currentIndex + 1} / {items.length}
          </span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-[#eceef2]">
          <div
            className="h-full bg-[#087f8c] transition-all duration-300"
            style={{
              width: `${((currentIndex + 1) / items.length) * 100}%`
            }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentItem.localId}
          initial={{ opacity: 0, x: direction * 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -20 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="space-y-6 rounded-lg border border-[#e7e9ee] bg-white p-6 shadow-sm"
        >
          <div>
            <p className="text-3xl font-semibold tracking-tight text-[#171717]">
              {currentItem.name}
            </p>
            <p className="mt-1 text-sm text-[#6f747c]">
              Par in {currentItem.stockUnit}
              {currentItem.packageSizeLabel
                ? ` — 1 ${currentItem.unit} = ${currentItem.packageSizeLabel}`
                : ''}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                void savePar(par - 1);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-[#e7e9ee] bg-white transition-colors hover:border-[#087f8c] hover:text-[#087f8c] disabled:opacity-50"
            >
              <Minus className="h-4 w-4" />
            </button>

            <span className="w-20 text-center text-5xl font-bold tabular-nums">
              {par}
            </span>

            <button
              type="button"
              disabled={saving}
              onClick={() => {
                void savePar(par + 1);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-[#e7e9ee] bg-white transition-colors hover:border-[#087f8c] hover:text-[#087f8c] disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
        <button
          type="button"
          onClick={handlePrev}
          className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] hover:bg-[#f0f2f5]"
        >
          {currentIndex === 0 ? 'Back' : 'Previous'}
        </button>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={handleNext}
            className="rounded-md border border-[#e7e9ee] bg-white px-3 py-2 text-sm font-medium text-[#171717] hover:bg-[#f0f2f5]"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white hover:bg-[#066873]"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};
